import React, { useState, useEffect } from 'react';
import { 
  Sparkles, Calendar, Clock, ArrowRight, Bot, RefreshCw, Search, CheckCircle2, 
  ChevronRight, X, Rss, BarChart3, TrendingUp, Activity, CheckSquare, ShieldCheck, 
  Zap, Layers, Cpu, Eye, FileCheck2, ListFilter, Award, Check
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string[];
  category: 'AI & Automation' | 'Typesetting & Prepress' | 'XML & JATS/BITS' | 'eBook & EPUB' | 'Accessibility (PDF/UA)';
  author: {
    name: string;
    role: string;
    avatarBg: string;
  };
  publishDate: string;
  weekNumber: number;
  readTime: string;
  tags: string[];
  isAutoGenerated: boolean;
  featured?: boolean;
}

const INITIAL_BLOG_POSTS: BlogPost[] = [
  {
    id: 'post-wk-29',
    title: 'How AI Automation Reduced Academic Journal Prepress Turnaround by 60% in 2026',
    slug: 'ai-automation-reduced-prepress-turnaround',
    excerpt: 'Explore how combining large language model structural parsing with Adobe InDesign server scripts streamlines complex STM journal typesetting from days to hours.',
    category: 'AI & Automation',
    author: {
      name: 'ASAP AI Publishing Engine',
      role: 'Automated Weekly Dispatch #29',
      avatarBg: 'from-blue-600 to-indigo-600'
    },
    publishDate: 'July 21, 2026',
    weekNumber: 29,
    readTime: '4 min read',
    tags: ['AI Publishing', 'Prepress', 'InDesign Automation', 'STM Journals'],
    isAutoGenerated: true,
    featured: true,
    content: [
      'The modern publishing landscape demands unprecedented speed without compromising on typographic rigor or structural accuracy. In academic and STM (Science, Technical, Medical) publishing, converting raw LaTeX or MS Word manuscripts into publication-ready PDF and validated XML has historically taken up to 7 business days per volume.',
      'By deploying ASAP LEARNING\'s AI-driven structural parsing pipeline, publishers can automatically detect article metadata, author affiliations, math formulas, figure captions, and reference citations upon manuscript submission. The extracted hierarchy is transformed directly into structured BITS/JATS XML while auto-populating InDesign master templates.',
      'The result is a 60% reduction in production turnaround, zero baseline grid alignment errors, and instant validation against PubMed Central and CrossRef DTD specifications.',
      'Key Benefits for Academic Publishers:\n1. Instant reference linking & DOI matching via automated cross-ref lookup.\n2. Automated math rendering into MathML and vector SVG.\n3. Real-time preflight checking for print and web compliance.'
    ]
  },
  {
    id: 'post-wk-28',
    title: 'Mastering JATS 1.3 and BITS XML Conversion for Medical and Scientific Books',
    slug: 'mastering-jats-bits-xml-conversion',
    excerpt: 'A comprehensive technical deep-dive into automated DTD validation, MathML encoding, and metadata enrichment for National Library of Medicine (NLM) indexing.',
    category: 'XML & JATS/BITS',
    author: {
      name: 'ASAP AI Publishing Engine',
      role: 'Automated Weekly Dispatch #28',
      avatarBg: 'from-emerald-600 to-teal-600'
    },
    publishDate: 'July 14, 2026',
    weekNumber: 28,
    readTime: '6 min read',
    tags: ['JATS XML', 'BITS 2.1', 'PubMed Central', 'MathML'],
    isAutoGenerated: true,
    content: [
      'JATS (Journal Article Tag Suite) and BITS (Book Interchange Tag Suite) are the cornerstone XML schemas for international medical databases, PMC archiving, and digital library repositories.',
      'Creating error-free JATS XML manually requires laborious tagging of nested lists, complex table cell spans, and intricate bibliographic citations. Our automated transformation engine parses raw manuscript files and outputs strict JATS 1.3 and BITS 2.1 XML with 99.9% structural fidelity.',
      'In addition, our validation workflow automatically checks for compliance against NLM and PubMed Central guidelines, embedding full article metadata and ORCID identifiers seamlessly.'
    ]
  },
  {
    id: 'post-wk-27',
    title: 'Achieving 100% PDF/UA and WCAG 2.1 AA Compliance in Digital Publications',
    slug: 'pdf-ua-wcag-accessibility-guide',
    excerpt: 'Step-by-step best practices for automated heading structure, screen-reader logical reading order, and AI-generated descriptive alt-text for complex charts.',
    category: 'Accessibility (PDF/UA)',
    author: {
      name: 'ASAP AI Publishing Engine',
      role: 'Automated Weekly Dispatch #27',
      avatarBg: 'from-purple-600 to-pink-600'
    },
    publishDate: 'July 07, 2026',
    weekNumber: 27,
    readTime: '5 min read',
    tags: ['PDF/UA', 'WCAG 2.1', 'Accessibility', 'Screen Readers'],
    isAutoGenerated: true,
    content: [
      'Digital accessibility is no longer optional—global mandates like the European Accessibility Act (EAA) and Section 508 require all educational textbooks, corporate reports, and government documents to be fully screen-reader accessible.',
      'ASAP LEARNING utilizes automated AI visual analysis to inspect every image, chart, and infographic, automatically writing contextual alternative text (alt-text) suitable for visually impaired readers.',
      'Our tagging system ensures logical reading order, proper table headers, contrast ratio verification, and full PDF/UA artifact marking for non-decorative elements.'
    ]
  },
  {
    id: 'post-wk-26',
    title: 'Reflowable vs. Fixed-Layout EPUB 3.2: Best Practices for Educational Courseware',
    slug: 'reflowable-vs-fixed-epub-3-educational-books',
    excerpt: 'Learn when to choose reflowable EPUB for text-heavy textbooks or fixed-layout for rich multimedia children\'s books and STEM workbooks.',
    category: 'eBook & EPUB',
    author: {
      name: 'ASAP AI Publishing Engine',
      role: 'Automated Weekly Dispatch #26',
      avatarBg: 'from-amber-600 to-orange-600'
    },
    publishDate: 'June 30, 2026',
    weekNumber: 26,
    readTime: '4 min read',
    tags: ['EPUB3', 'eBooks', 'EdTech', 'Kindle Format'],
    isAutoGenerated: true,
    content: [
      'Choosing between reflowable and fixed-layout EPUB 3.2 impacts reader experience across smartphones, tablets, and e-readers. While reflowable text accommodates custom font sizes and dark mode, fixed-layout maintains exact page geometry for complex multi-column designs.',
      'At ASAP LEARNING, our automated dual-stream pipeline outputs both reflowable EPUB for optimal mobile reading and interactive fixed-layout editions enriched with pop-up media overlays.'
    ]
  }
];

const CATEGORIES = [
  'All Articles',
  'AI & Automation',
  'Typesetting & Prepress',
  'XML & JATS/BITS',
  'eBook & EPUB',
  'Accessibility (PDF/UA)'
];

const WEEKLY_AUTOMATION_STATS = [
  { week: 'W25', autoPublished: 1, successRate: 100, readerViews: 1840, latencySec: 1.6, schemaCompliance: 100 },
  { week: 'W26', autoPublished: 1, successRate: 100, readerViews: 2210, latencySec: 1.5, schemaCompliance: 100 },
  { week: 'W27', autoPublished: 1, successRate: 100, readerViews: 2750, latencySec: 1.4, schemaCompliance: 100 },
  { week: 'W28', autoPublished: 1, successRate: 100, readerViews: 3200, latencySec: 1.3, schemaCompliance: 100 },
  { week: 'W29', autoPublished: 1, successRate: 100, readerViews: 3950, latencySec: 1.2, schemaCompliance: 100 },
  { week: 'W30', autoPublished: 1, successRate: 100, readerViews: 4520, latencySec: 1.1, schemaCompliance: 100 },
];

const AUTOMATED_CATEGORY_DISTRIBUTION = [
  { name: 'AI & Automation', count: 12, percentage: 40, color: '#3B82F6' },
  { name: 'Typesetting & Prepress', count: 8, percentage: 26, color: '#6366F1' },
  { name: 'XML & JATS/BITS', count: 5, percentage: 17, color: '#8B5CF6' },
  { name: 'Accessibility (PDF/UA)', count: 3, percentage: 10, color: '#10B981' },
  { name: 'eBook & EPUB', count: 2, percentage: 7, color: '#F59E0B' },
];

export const BlogSection: React.FC<{ onOpenContact?: () => void }> = ({ onOpenContact }) => {
  const [posts, setPosts] = useState<BlogPost[]>(() => {
    try {
      const isReset = localStorage.getItem('asap_is_reset') === 'true';
      if (isReset) return INITIAL_BLOG_POSTS;
      const saved = localStorage.getItem('asap_blog_posts');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.warn('Failed to parse asap_blog_posts from localStorage', e);
    }
    return INITIAL_BLOG_POSTS;
  });

  const [activeCategory, setActiveCategory] = useState<string>('All Articles');
  const [activeViewMode, setActiveViewMode] = useState<'feed' | 'dashboard'>('feed');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  // Generator State
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationStep, setGenerationStep] = useState<string>('');
  const [topicInput, setTopicInput] = useState<string>('');
  const [genSuccessMessage, setGenSuccessMessage] = useState<string>('');

  // Countdown timer calculation to next weekly publication
  const [timeToNextRelease, setTimeToNextRelease] = useState({ days: 4, hours: 18, mins: 32, secs: 45 });

  // 1. Hydrate & Sync with Server, Auto-check weekly generation on mount & real-time live sync
  useEffect(() => {
    const syncAndCheckWeekly = async () => {
      try {
        const isReset = localStorage.getItem('asap_is_reset') === 'true';
        if (isReset) return;

        // Fetch server stored blogs
        const res = await fetch('/api/blogs');
        const data = await res.json();
        if (data.success && Array.isArray(data.posts) && data.posts.length > 0) {
          setPosts(prev => {
            const map = new Map<string, BlogPost>();
            prev.forEach(p => map.set(p.id, p));
            data.posts.forEach((p: BlogPost) => map.set(p.id, p));
            const merged = Array.from(map.values()).sort((a, b) => (b.weekNumber || 0) - (a.weekNumber || 0));
            try {
              localStorage.setItem('asap_blog_posts', JSON.stringify(merged));
            } catch (e) {
              console.warn('LocalStorage save failed', e);
            }
            return merged;
          });
        }

        // Check if automated weekly generation needs to run
        const lastAutoRun = localStorage.getItem('asap_last_auto_blog_timestamp');
        const now = Date.now();
        const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

        if (!lastAutoRun || (now - parseInt(lastAutoRun, 10)) >= ONE_WEEK_MS) {
          console.log('⚡ Weekly Automated Publisher Engine: Triggering scheduled weekly dispatch...');
          handleTriggerAutomatedGeneration('Automated Weekly Publishing Insights & Industry Trends');
          localStorage.setItem('asap_last_auto_blog_timestamp', String(now));
        }
      } catch (err) {
        console.warn('Failed to sync or auto-check weekly blogs:', err);
      }
    };

    syncAndCheckWeekly();

    // Automatic Live Poll every 5 seconds to guarantee live updates without manual refresh
    const liveInterval = setInterval(syncAndCheckWeekly, 5000);
    const handleCustomUpdate = () => syncAndCheckWeekly();
    window.addEventListener('asap_blog_updated', handleCustomUpdate);

    return () => {
      clearInterval(liveInterval);
      window.removeEventListener('asap_blog_updated', handleCustomUpdate);
    };
  }, []);

  // 2. Local Storage Persistence Sync
  useEffect(() => {
    try {
      const isReset = localStorage.getItem('asap_is_reset') === 'true';
      if (!isReset) {
        localStorage.setItem('asap_blog_posts', JSON.stringify(posts));
      }
    } catch (e) {
      console.warn('Failed to save blog posts to localStorage', e);
    }
  }, [posts]);

  // 3. Live Interval Countdown Timer & Automatic Trigger Execution
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeToNextRelease(prev => {
        if (prev.secs > 0) return { ...prev, secs: prev.secs - 1 };
        if (prev.mins > 0) return { ...prev, mins: prev.mins - 1, secs: 59 };
        if (prev.hours > 0) return { ...prev, hours: prev.hours - 1, mins: 59, secs: 59 };
        if (prev.days > 0) return { ...prev, days: prev.days - 1, hours: 23, mins: 59, secs: 59 };

        // Timer reached 0! Trigger automatic weekly post generation live!
        console.log('⏰ Weekly Automation Timer Expired! Auto-generating next weekly post live...');
        setTimeout(() => {
          handleTriggerAutomatedGeneration('Automated Weekly Dispatch: Next-Gen Prepress & AI XML Workflows');
        }, 100);

        return { days: 6, hours: 23, mins: 59, secs: 59 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [posts]);

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesCat = activeCategory === 'All Articles' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCat && matchesSearch;
  });

  // Automated AI Blog Generator Execution
  const handleTriggerAutomatedGeneration = async (overrideTopic?: string) => {
    setIsGenerating(true);
    setGenSuccessMessage('');

    setGenerationStep('Scanning STM & Academic publishing industry news...');
    await new Promise(r => setTimeout(r, 600));

    setGenerationStep('Formatting technical code snippets & JATS 1.3 XML outline...');
    await new Promise(r => setTimeout(r, 700));

    setGenerationStep('Calling ASAP AI Publishing Model (Gemini 3.6 Flash)...');

    const nextWeekNum = posts.length > 0 ? Math.max(...posts.map(p => p.weekNumber || 29)) + 1 : 30;
    const todayFormatted = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    const topic = (typeof overrideTopic === 'string' && overrideTopic)
      ? overrideTopic
      : (topicInput.trim() || 'Next-Gen Automated XML Transformation & AI InDesign Scripting');

    let newPost: BlogPost | null = null;

    try {
      const res = await fetch('/api/generate-blog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic })
      });
      const resData = await res.json();

      if (resData.success && resData.post) {
        newPost = resData.post;
      } else if (resData.success && resData.data) {
        const parsed = resData.data;
        let selectedCat: BlogPost['category'] = 'AI & Automation';
        if (parsed.category && CATEGORIES.includes(parsed.category)) {
          selectedCat = parsed.category;
        }

        newPost = {
          id: `post-wk-${nextWeekNum}-${Date.now()}`,
          title: parsed.title || `${topic} — Automated Weekly Insights`,
          slug: (parsed.title || topic).toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          excerpt: parsed.excerpt || `Latest automated technical dispatch exploring ${topic} in prepress publishing.`,
          category: selectedCat,
          author: {
            name: 'ASAP AI Publishing Engine',
            role: `Automated Weekly Dispatch #${nextWeekNum}`,
            avatarBg: 'from-blue-600 via-indigo-600 to-purple-600'
          },
          publishDate: todayFormatted,
          weekNumber: nextWeekNum,
          readTime: '4 min read',
          tags: parsed.tags || ['Auto-Published', 'AI Automation', 'Prepress Tech', 'Weekly Insights'],
          isAutoGenerated: true,
          content: parsed.content || [parsed.excerpt]
        };
      } else {
        throw new Error(resData.error || 'Server generator fallback triggered');
      }
    } catch (err) {
      console.log('AI server generation fallback:', err);
      newPost = {
        id: `post-wk-${nextWeekNum}-${Date.now()}`,
        title: topicInput.trim()
          ? `Automated Guide to ${topicInput.trim()}: Prepress & Production Innovations`
          : `Weekly Dispatch #${nextWeekNum}: Automating Complex Mathematical Formula Layouts in LaTeX & InDesign`,
        slug: `weekly-dispatch-${nextWeekNum}`,
        excerpt: `An in-depth automated report on streamlining scientific equation formatting, MathML tagging, and zero-error EPS vector graphic rendering for STM publications.`,
        category: 'Typesetting & Prepress',
        author: {
          name: 'ASAP AI Publishing Engine',
          role: `Automated Weekly Dispatch #${nextWeekNum}`,
          avatarBg: 'from-blue-600 via-indigo-600 to-purple-600'
        },
        publishDate: todayFormatted,
        weekNumber: nextWeekNum,
        readTime: '4 min read',
        tags: ['Auto-Published', 'AI Automation', 'Prepress Tech', 'Weekly Insights'],
        isAutoGenerated: true,
        content: [
          `Automated mathematical layout remains one of the most technical challenges in scientific and academic publishing. Equations containing nested fractions, matrices, and special Greek symbols require absolute precision to ensure legibility across print and digital formats.`,
          `Using ASAP LEARNING's AI-assisted MathML parsing engine, TeX/LaTeX equation blocks are automatically parsed, converted into crisp SVG/EPS vectors for InDesign layouts, and tagged as semantic MathML within JATS 1.3 XML outputs.`,
          `This automated workflow eliminates manual math re-keying, reduces proofing errors by 95%, and guarantees screen-reader speech compliance for visually impaired scholars.`
        ]
      };
    }

    setGenerationStep('Validating metadata & auto-publishing to feed...');
    await new Promise(r => setTimeout(r, 500));

    if (newPost) {
      setActiveCategory('All Articles');
      setSearchQuery('');
      setPosts(prev => {
        const updated = [newPost!, ...prev.filter(p => p.id !== newPost!.id)];
        try {
          localStorage.setItem('asap_blog_posts', JSON.stringify(updated));
          localStorage.setItem('asap_last_auto_blog_timestamp', String(Date.now()));
        } catch (e) {
          console.warn('Failed to update localStorage', e);
        }
        return updated;
      });

      // Mirror to server and notify listeners
      fetch('/api/blogs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPost)
      }).then(() => {
        window.dispatchEvent(new CustomEvent('asap_blog_updated'));
      }).catch(e => console.warn('Server blog sync warning:', e));
    }

    setIsGenerating(false);
    setTopicInput('');
    setGenSuccessMessage(`Success! Weekly Post #${nextWeekNum} has been automatically generated and published.`);
    setTimeToNextRelease({ days: 6, hours: 23, mins: 59, secs: 59 });
  };

  return (
    <section id="blog" className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background Glow Effect */}
      <div className="absolute top-0 right-1/3 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-purple-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold tracking-wide uppercase mb-4">
            <Sparkles className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
            <span>100% Automated Weekly Publishing Engine</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
            Automated Publishing & Prepress Blog
          </h2>
          <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed">
            Our AI content engine automatically writes and publishes a fresh technical article <strong className="text-blue-300">every single week</strong> on typesetting, XML schemas, accessibility, and prepress innovations.
          </p>
        </div>

        {/* Weekly Automated Scheduler Status Banner */}
        <div className="max-w-5xl mx-auto mb-12 bg-gradient-to-r from-slate-800/90 via-slate-800 to-indigo-950/80 rounded-2xl p-6 border border-slate-700/80 shadow-2xl backdrop-blur-md">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">

            {/* Left Engine Info */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-lg shadow-blue-500/20">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-emerald-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    Automated Engine Active
                  </span>
                  <span className="text-xs text-slate-400">• Scheduled Weekly</span>
                </div>
                <h3 className="text-lg font-bold text-white">
                  Next Automated Post in {timeToNextRelease.days}d {timeToNextRelease.hours}h {timeToNextRelease.mins}m {timeToNextRelease.secs}s
                </h3>
                <p className="text-xs text-slate-300">
                  Every Monday at 09:00 AM UTC, our automated engine scans global STM publishing trends and auto-publishes technical articles.
                </p>
              </div>
            </div>

            {/* Right Trigger Control Box */}
            <div className="w-full lg:w-auto bg-slate-900/80 p-4 rounded-xl border border-slate-700/60 space-y-3 shrink-0">
              <div className="flex items-center justify-between gap-3 text-xs text-slate-300">
                <span className="font-semibold text-slate-200">Instant AI Generator:</span>
                <span className="text-[10px] text-blue-400 font-mono">Gemini 3.6 Flash</span>
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Custom topic or press enter for auto..."
                  value={topicInput}
                  onChange={(e) => setTopicInput(e.target.value)}
                  disabled={isGenerating}
                  className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs text-white placeholder:text-slate-500 outline-none focus:border-blue-500 w-full lg:w-60"
                />
                <button
                  onClick={handleTriggerAutomatedGeneration}
                  disabled={isGenerating}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-xs font-bold transition-all shadow-md inline-flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Writing...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5 text-blue-300" />
                      <span>Trigger AI Blog Now</span>
                    </>
                  )}
                </button>
              </div>

              {isGenerating && (
                <div className="text-[11px] text-blue-300 font-medium animate-pulse flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  {generationStep}
                </div>
              )}

              {genSuccessMessage && !isGenerating && (
                <div className="text-[11px] text-emerald-400 font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span>{genSuccessMessage}</span>
                </div>
              )}
            </div>

          </div>
        </div>



        {/* RECHARTS AUTOMATION DASHBOARD VIEW */}
        {activeViewMode === 'dashboard' ? (
          <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-300">
            {/* Top 4 KPI Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-800/90 border border-slate-700/80 rounded-2xl p-5 shadow-xl relative overflow-hidden">
                <div className="flex items-center justify-between text-slate-400 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">Weekly Success Rate</span>
                  <div className="w-8 h-8 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                    <CheckSquare className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-3xl font-black text-white">100.0%</div>
                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1 font-semibold">
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span>30 / 30 Dispatches Published On-Time</span>
                </p>
              </div>

              <div className="bg-slate-800/90 border border-slate-700/80 rounded-2xl p-5 shadow-xl relative overflow-hidden">
                <div className="flex items-center justify-between text-slate-400 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">Cron Schedule Uptime</span>
                  <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                    <Clock className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-3xl font-black text-white">100.0%</div>
                <p className="text-xs text-blue-300 mt-2 font-medium">Every Monday @ 09:00 AM UTC</p>
              </div>

              <div className="bg-slate-800/90 border border-slate-700/80 rounded-2xl p-5 shadow-xl relative overflow-hidden">
                <div className="flex items-center justify-between text-slate-400 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">Avg AI Synthesis Time</span>
                  <div className="w-8 h-8 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400">
                    <Zap className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-3xl font-black text-white">1.1s</div>
                <p className="text-xs text-purple-300 mt-2 font-medium">Powered by Gemini 3.6 Flash</p>
              </div>

              <div className="bg-slate-800/90 border border-slate-700/80 rounded-2xl p-5 shadow-xl relative overflow-hidden">
                <div className="flex items-center justify-between text-slate-400 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">Schema Validation</span>
                  <div className="w-8 h-8 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-3xl font-black text-white">100.0%</div>
                <p className="text-xs text-indigo-300 mt-2 font-medium">JATS 1.3 XML & MathML Validated</p>
              </div>
            </div>

            {/* Recharts Main Chart 1: Weekly Auto-Generation & Reader Traffic Area Chart */}
            <div className="bg-slate-800/90 border border-slate-700/80 rounded-3xl p-6 shadow-2xl space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-700/80 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                    <span>Weekly Auto-Generation Volume & Reader Impressions Trend</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    Tracking 100% automated release velocity and growing technical readership engagement across weeks.
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1.5 text-blue-400 font-semibold">
                    <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" /> Reader Views
                  </span>
                  <span className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                    <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> Success Rate (100%)
                  </span>
                </div>
              </div>

              <div className="h-72 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={WEEKLY_AUTOMATION_STATS} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="week" stroke="#94A3B8" fontSize={12} tickLine={false} />
                    <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0F172A', borderColor: '#334155', borderRadius: '12px', color: '#F8FAFC', fontSize: '12px' }}
                      itemStyle={{ color: '#F8FAFC' }}
                    />
                    <Area type="monotone" dataKey="readerViews" name="Reader Impressions" stroke="#3B82F6" strokeWidth={3} fillOpacity={1} fill="url(#colorViews)" />
                    <Area type="monotone" dataKey="successRate" name="Success Rate (%)" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorSuccess)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recharts Grid 2: Category Distribution & Performance Telemetry */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category Bar Chart */}
              <div className="bg-slate-800/90 border border-slate-700/80 rounded-3xl p-6 shadow-2xl space-y-4">
                <div className="border-b border-slate-700/80 pb-3">
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Layers className="w-4 h-4 text-purple-400" />
                    <span>Automated Articles by Technical Category</span>
                  </h3>
                  <p className="text-xs text-slate-400">Distribution of topics auto-published by the engine</p>
                </div>

                <div className="h-60 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={AUTOMATED_CATEGORY_DISTRIBUTION} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                      <XAxis type="number" stroke="#94A3B8" fontSize={11} />
                      <YAxis dataKey="name" type="category" stroke="#94A3B8" fontSize={11} width={130} tickLine={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#0F172A', borderColor: '#334155', borderRadius: '12px', color: '#F8FAFC', fontSize: '12px' }}
                      />
                      <Bar dataKey="count" name="Published Articles" radius={[0, 8, 8, 0]}>
                        {AUTOMATED_CATEGORY_DISTRIBUTION.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Live Engine Health & Validation Checklist */}
              <div className="bg-slate-800/90 border border-slate-700/80 rounded-3xl p-6 shadow-2xl space-y-4">
                <div className="border-b border-slate-700/80 pb-3 flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-cyan-400" />
                      <span>Engine Health & Preflight Quality Checks</span>
                    </h3>
                    <p className="text-xs text-slate-400">Automated pre-publication validation rules</p>
                  </div>
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2.5 py-1 rounded-full font-bold">
                    ALL GREEN
                  </span>
                </div>

                <div className="space-y-3 pt-1">
                  <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-700/60 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-white">JATS 1.3 & BITS 2.1 DTD Validation</p>
                        <p className="text-[11px] text-slate-400">Strict structural XML syntax check</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-400 font-mono">100% Passed</span>
                  </div>

                  <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-700/60 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-white">MathML 3.0 Formula Vector Render</p>
                        <p className="text-[11px] text-slate-400">Inline and display equation formatting</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-400 font-mono">100% Passed</span>
                  </div>

                  <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-700/60 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-white">WCAG 2.1 AA Screen-Reader Speech Tagging</p>
                        <p className="text-[11px] text-slate-400">Alt text & reading order verification</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-400 font-mono">100% Passed</span>
                  </div>

                  <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-700/60 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-white">Automatic RSS & Sitemap Indexing</p>
                        <p className="text-[11px] text-slate-400">Search engine indexing dispatch</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-400 font-mono">100% Passed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Automated Dispatches Audit Log */}
            <div className="bg-slate-800/90 border border-slate-700/80 rounded-3xl p-6 shadow-2xl space-y-4">
              <div className="flex items-center justify-between border-b border-slate-700/80 pb-4">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <FileCheck2 className="w-4 h-4 text-blue-400" />
                    <span>Recent Weekly Automated Dispatches Audit Log</span>
                  </h3>
                  <p className="text-xs text-slate-400">Live feed of automatically generated technical dispatches</p>
                </div>
                <button
                  onClick={() => setActiveViewMode('feed')}
                  className="text-xs text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1"
                >
                  <span>View All in Feed</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-700 text-slate-400 font-bold uppercase tracking-wider">
                      <th className="py-3 px-3">Dispatch</th>
                      <th className="py-3 px-3">Article Title</th>
                      <th className="py-3 px-3">Category</th>
                      <th className="py-3 px-3">AI Engine</th>
                      <th className="py-3 px-3">Latency</th>
                      <th className="py-3 px-3">XML Validation</th>
                      <th className="py-3 px-3 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/60 text-slate-300">
                    {posts.slice(0, 5).map((post) => (
                      <tr key={post.id} className="hover:bg-slate-700/40 transition-colors">
                        <td className="py-3 px-3 font-mono font-bold text-blue-400">#{post.weekNumber}</td>
                        <td className="py-3 px-3 font-semibold text-white max-w-xs truncate">{post.title}</td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 rounded-md bg-slate-700 text-slate-300 text-[11px]">
                            {post.category}
                          </span>
                        </td>
                        <td className="py-3 px-3 font-mono text-indigo-300">Gemini 3.6 Flash</td>
                        <td className="py-3 px-3 font-mono text-slate-400">1.2s</td>
                        <td className="py-3 px-3 font-semibold text-emerald-400 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Passed
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-extrabold border border-emerald-500/30">
                            AUTO-PUBLISHED
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Filter Controls: Search & Categories */}
            <div className="max-w-5xl mx-auto mb-10 space-y-6">
              {/* Search Input */}
              <div className="relative">
                <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search automated articles by keyword (e.g., InDesign, JATS XML, PDF/UA, EPUB)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 bg-slate-800/80 rounded-xl border border-slate-700 text-white placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-white"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Category Chips */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none" role="tablist" aria-label="Blog category filters">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    role="tab"
                    aria-selected={activeCategory === cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                      activeCategory === cat
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                        : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 border border-slate-700'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Featured Post Hero Card */}
        {activeCategory === 'All Articles' && !searchQuery && posts.length > 0 && (
          <div className="max-w-5xl mx-auto mb-12">
            <div
              onClick={() => setSelectedPost(posts[0])}
              className="group bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-700/80 hover:border-blue-500/50 transition-all duration-300 cursor-pointer shadow-xl hover:shadow-2xl hover:shadow-blue-500/10 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none group-hover:bg-blue-500/20 transition-all" />

              <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between">
                <div className="space-y-4 max-w-3xl">
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 font-bold border border-blue-400/30 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-blue-400" />
                      Latest Weekly Dispatch #{posts[0].weekNumber}
                    </span>
                    <span className="text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-slate-500" />
                      {posts[0].publishDate}
                    </span>
                    <span className="text-slate-400 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-slate-500" />
                      {posts[0].readTime}
                    </span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-extrabold text-white group-hover:text-blue-300 transition-colors leading-snug">
                    {posts[0].title}
                  </h3>

                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed line-clamp-3">
                    {posts[0].excerpt}
                  </p>

                  <div className="flex flex-wrap gap-2 pt-2">
                    {posts[0].tags.map((tag, idx) => (
                      <span key={idx} className="px-2.5 py-0.5 rounded-md bg-slate-700/60 text-slate-300 text-[11px] font-medium border border-slate-600/50">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="shrink-0 flex md:flex-col items-center md:items-end justify-between md:justify-center gap-4 pt-4 md:pt-0 border-t md:border-t-0 border-slate-700/80">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-tr ${posts[0].author.avatarBg} flex items-center justify-center text-white text-xs font-bold`}>
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white">{posts[0].author.name}</div>
                      <div className="text-[10px] text-blue-300">{posts[0].author.role}</div>
                    </div>
                  </div>

                  <button className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition-all inline-flex items-center gap-1.5 shadow-md">
                    <span>Read Article</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Grid of Blog Posts */}
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                onClick={() => setSelectedPost(post)}
                className="group bg-slate-800/80 hover:bg-slate-800 rounded-2xl p-6 border border-slate-700/80 hover:border-blue-500/50 transition-all duration-300 cursor-pointer flex flex-col justify-between shadow-lg hover:shadow-xl hover:-translate-y-1"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[11px] text-slate-400">
                    <span className="px-2.5 py-0.5 rounded-md bg-blue-500/10 text-blue-300 font-bold border border-blue-500/20">
                      {post.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-slate-500" />
                      {post.publishDate}
                    </span>
                  </div>

                  <h4 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors leading-snug line-clamp-2">
                    {post.title}
                  </h4>

                  <p className="text-xs text-slate-300 leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                </div>

                <div className="pt-5 mt-4 border-t border-slate-700/60 flex items-center justify-between text-xs text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <Bot className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-[11px] text-slate-300">Wk #{post.weekNumber} Auto-Dispatch</span>
                  </div>

                  <span className="font-bold text-blue-400 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                    Read <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="bg-slate-800/60 rounded-2xl p-8 text-center border border-slate-700">
              <p className="text-slate-400 text-sm">No automated articles found matching your criteria.</p>
              <button
                onClick={() => { setSearchQuery(''); setActiveCategory('All Articles'); }}
                className="mt-3 text-xs font-bold text-blue-400 hover:underline"
              >
                Reset search filters
              </button>
            </div>
          )}
        </div>

        {/* Subscribe Box */}
        <div className="max-w-5xl mx-auto mt-16 bg-gradient-to-r from-blue-900/60 via-slate-800 to-purple-900/60 rounded-2xl p-6 sm:p-8 border border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <Rss className="w-4 h-4 text-amber-400" />
              <h4 className="text-base font-extrabold text-white">Subscribe to Weekly Automated Publishing Dispatches</h4>
            </div>
            <p className="text-xs text-slate-300">Get every weekly automated article on prepress, XML, and accessibility delivered to your inbox.</p>
          </div>

          <div className="flex gap-2 w-full sm:w-auto">
            <input
              type="email"
              placeholder="Enter your publisher email..."
              className="px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-xs text-white placeholder:text-slate-500 outline-none focus:border-blue-500 w-full sm:w-64"
            />
            <button
              onClick={() => alert('Subscribed! You will receive our automated weekly publishing dispatch every Monday.')}
              className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition-all shrink-0 shadow-md"
            >
              Subscribe
            </button>
          </div>
        </div>

      </div>

      {/* Full Article Reading Modal */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 relative shadow-2xl animate-in zoom-in-95 duration-200">

            <button
              onClick={() => setSelectedPost(null)}
              className="absolute top-6 right-6 p-2 rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4 pr-8">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 font-bold border border-blue-400/30">
                  {selectedPost.category}
                </span>
                <span className="text-slate-400">• {selectedPost.publishDate}</span>
                <span className="text-slate-400">• {selectedPost.readTime}</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight">
                {selectedPost.title}
              </h2>

              <div className="flex items-center gap-3 pt-2 pb-4 border-b border-slate-800">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-tr ${selectedPost.author.avatarBg} flex items-center justify-center text-white text-xs font-bold`}>
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs font-bold text-white">{selectedPost.author.name}</div>
                  <div className="text-[11px] text-blue-300">{selectedPost.author.role}</div>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed">
              {selectedPost.content.map((paragraph, i) => (
                <p key={i} className="whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-slate-800 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                {selectedPost.tags.map((tag, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-lg bg-slate-800 text-slate-400 text-xs font-medium">
                    #{tag}
                  </span>
                ))}
              </div>

              {onOpenContact && (
                <button
                  onClick={() => {
                    setSelectedPost(null);
                    onOpenContact();
                  }}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold transition-all shadow-md inline-flex items-center gap-2"
                >
                  <span>Discuss This Solution with ASAP Team</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>

          </div>
        </div>
      )}

    </section>
  );
};
